import React from 'react';
import { useAuth } from 'react-oidc-context';
import Button from './Button';
import { buildSignUpUrl } from '../lib/cognitoUrls';

export interface GuestAccountHint {
  email: string;
  accountExists: boolean;
}

interface GuestAccountPromptProps {
  hint: GuestAccountHint;
}

/**
 * Post-purchase CTA for a guest checkout, shown once the order is confirmed.
 *
 * The email a guest typed at checkout is never trusted for linking — see
 * user.service.ts's getOrCreateProfile — but it's a fine signal for which CTA
 * to show: if an account already exists for it, offer to log in (a signup
 * would just be rejected by Cognito as a duplicate); otherwise offer to
 * create one. Either way the actual linking happens server-side, keyed off
 * the *verified* Cognito email once the guest signs in.
 */
const GuestAccountPrompt: React.FC<GuestAccountPromptProps> = ({ hint }) => {
  const auth = useAuth();

  if (hint.accountExists) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          You already have an account
        </h3>
        <p className="text-blue-800 text-sm mb-4">
          Log in to see this booking alongside your other rentals.
        </p>
        <Button
          onClick={() => void auth.signinRedirect({ login_hint: hint.email })}
        >
          Log In
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-blue-900 mb-2">
        Create an account to manage this booking
      </h3>
      <p className="text-blue-800 text-sm mb-4">
        Save this booking to an account so you can view it later, get updates, and check out
        faster next time.
      </p>
      <Button
        onClick={() => {
          window.location.href = buildSignUpUrl({ loginHint: hint.email });
        }}
      >
        Create Account
      </Button>
    </div>
  );
};

export default GuestAccountPrompt;
